import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, Checkbox, CircularProgress, FormControlLabel, MenuItem, TextField } from '@mui/material';
import Group from 'components/common/Group';
import { renderField } from 'components/utils';
import { fetchLinkedInSkillSuggestions } from './fetchLinkedInSkillSuggestions';
import { fetchLinkedInLocationSuggestions } from './fetchLinkedInLocationSuggestions';
import { useExtensionDetection } from 'lib/useExtensionDetection';
import ExtensionLocationAutocomplete from './ExtensionLocationAutocomplete';
import { RecruiterLiteFields } from '../types';
import {
  recruiterLiteApplyMethodOptions,
  recruiterLiteEmploymentTypeOptions,
  recruiterLiteJobFunctionChoices,
  recruiterLiteSeniorityOptions,
  recruiterLiteWorkplaceOptions,
} from './recruiterLiteConstants';

type Props = {
  value: RecruiterLiteFields;
  onChange: (value: RecruiterLiteFields) => void;
  infoAlert?: React.ReactNode;
  wrapInGroup?: boolean;
  hideGroupTitle?: boolean;
};

const SKILL_SEARCH_DEBOUNCE_MS = 1000;
const SKILL_SEARCH_MIN_CHARS = 2;

type LinkedInSkillsAutocompleteProps = {
  value: string[];
  onChange: (next: string[]) => void;
  extensionInstalled: boolean;
};

const LinkedInSkillsAutocomplete = ({ value, onChange, extensionInstalled }: LinkedInSkillsAutocompleteProps) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const trimmedInput = inputValue.trim();

    if (!extensionInstalled || trimmedInput.length < SKILL_SEARCH_MIN_CHARS) {
      setOptions([]);
      setLoading(false);
      setSearchError(null);
      return;
    }

    const currentRequestId = requestIdRef.current + 1;
    requestIdRef.current = currentRequestId;

    const timer = window.setTimeout(async () => {
      setLoading(true);
      setSearchError(null);

      const result = await fetchLinkedInSkillSuggestions(trimmedInput);

      if (requestIdRef.current !== currentRequestId) {
        return;
      }

      setLoading(false);

      if (result.extensionNotRunning) {
        setOptions([]);
        setSearchError(result.error || null);
        return;
      }

      if (!result.ok) {
        setOptions([]);
        setSearchError(result.error || 'LinkedIn skill search failed');
        return;
      }

      setOptions(result.skills || []);
    }, SKILL_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [extensionInstalled, inputValue]);

  const noOptionsText = !extensionInstalled
    ? 'Install the Harbor extension to search LinkedIn skills'
    : inputValue.trim().length < SKILL_SEARCH_MIN_CHARS
      ? 'Type at least 2 characters'
      : searchError || 'No matching skills found';

  return (
    <Autocomplete
      multiple
      options={options}
      value={value}
      inputValue={inputValue}
      loading={loading}
      filterOptions={skillOptions => skillOptions}
      filterSelectedOptions
      disableCloseOnSelect
      getOptionLabel={option => option}
      isOptionEqualToValue={(option, optionValue) => option === optionValue}
      onInputChange={(_, nextInputValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setInputValue(nextInputValue);
        }
      }}
      onChange={(_, next) => onChange(next.slice(0, 10))}
      noOptionsText={noOptionsText}
      renderInput={params => (
        <TextField
          {...params}
          size="small"
          placeholder="Search LinkedIn skills (up to 10)"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const RecruiterLiteFieldsForm = ({
  value,
  onChange,
  infoAlert,
  wrapInGroup = true,
  hideGroupTitle = false,
}: Props) => {
  const { installed: extensionInstalled } = useExtensionDetection();
  const flexOptions = {
    label: 3,
    value: 9,
  };

  const updateField = <K extends keyof RecruiterLiteFields>(field: K, fieldValue: RecruiterLiteFields[K]) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  const renderLinkedInMultiAutocomplete = (
    options: readonly string[],
    selected: string[],
    maxItems: number,
    placeholder: string,
    onUpdate: (next: string[]) => void,
  ) => (
    <Autocomplete
      multiple
      options={[...options]}
      value={selected}
      onChange={(_, next) => onUpdate(next.slice(0, maxItems))}
      filterSelectedOptions
      disableCloseOnSelect
      getOptionLabel={option => option}
      isOptionEqualToValue={(option, optionValue) => option === optionValue}
      renderInput={params => <TextField {...params} size="small" placeholder={placeholder} />}
    />
  );

  const fields = (
    <>
      {infoAlert}
      {renderField({
        id: 'job_form_recruiter_lite_workplace',
        label: 'Workplace type',
        flexOptions,
        renderControl: () => (
          <TextField
            select
            fullWidth
            size="small"
            value={value.workplaceType || ''}
            onChange={e =>
              updateField('workplaceType', (e.target.value || undefined) as RecruiterLiteFields['workplaceType'])
            }
          >
            <MenuItem value="">None</MenuItem>
            {recruiterLiteWorkplaceOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ),
      })}

      {renderField({
        id: 'job_form_recruiter_lite_employee_location',
        label: 'Job location',
        flexOptions,
        renderControl: () => (
          <ExtensionLocationAutocomplete
            value={value.employeeLocation || ''}
            onChange={next => updateField('employeeLocation', next || undefined)}
            extensionInstalled={extensionInstalled}
            platformLabel="LinkedIn"
            placeholder="Search LinkedIn job location"
            searchKey={value.workplaceType === 'Remote'}
            fetchSuggestions={query => fetchLinkedInLocationSuggestions(query, value.workplaceType === 'Remote')}
          />
        ),
      })}

      {renderField({
        id: 'job_form_recruiter_lite_employment_type',
        label: 'Employment type',
        flexOptions,
        renderControl: () => (
          <TextField
            select
            fullWidth
            size="small"
            value={value.employmentType || ''}
            onChange={e =>
              updateField('employmentType', (e.target.value || undefined) as RecruiterLiteFields['employmentType'])
            }
          >
            <MenuItem value="">None</MenuItem>
            {recruiterLiteEmploymentTypeOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ),
      })}

      {renderField({
        id: 'job_form_recruiter_lite_seniority',
        label: 'Seniority level',
        flexOptions,
        renderControl: () => (
          <TextField
            select
            fullWidth
            size="small"
            value={value.seniorityLevel || ''}
            onChange={e =>
              updateField('seniorityLevel', (e.target.value || undefined) as RecruiterLiteFields['seniorityLevel'])
            }
          >
            <MenuItem value="">None</MenuItem>
            {recruiterLiteSeniorityOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ),
      })}

      {renderField({
        id: 'job_form_recruiter_lite_functions',
        label: 'Job functions',
        flexOptions,
        renderControl: () =>
          renderLinkedInMultiAutocomplete(
            recruiterLiteJobFunctionChoices,
            value.jobFunctions || [],
            3,
            'Search job functions (up to 3)',
            next => updateField('jobFunctions', next),
          ),
      })}

      {renderField({
        id: 'job_form_recruiter_lite_skills',
        label: 'Skills',
        flexOptions,
        renderControl: () => (
          <LinkedInSkillsAutocomplete
            value={value.skills || []}
            onChange={next => updateField('skills', next)}
            extensionInstalled={extensionInstalled}
          />
        ),
      })}

      {renderField({
        id: 'job_form_recruiter_lite_apply_method',
        label: 'Apply method',
        flexOptions,
        renderControl: () => (
          <TextField
            select
            fullWidth
            size="small"
            value={value.applyMethod || 'internal'}
            onChange={e => updateField('applyMethod', e.target.value as RecruiterLiteFields['applyMethod'])}
          >
            {recruiterLiteApplyMethodOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ),
      })}

      {value.applyMethod === 'external'
        ? renderField({
            id: 'job_form_recruiter_lite_external_url',
            label: 'External apply URL',
            flexOptions,
            renderControl: () => (
              <TextField
                fullWidth
                size="small"
                value={value.externalApplyUrl || ''}
                onChange={e => updateField('externalApplyUrl', e.target.value)}
              />
            ),
          })
        : renderField({
            id: 'job_form_recruiter_lite_apply_email',
            label: 'Apply email',
            flexOptions,
            renderControl: () => (
              <Box>
                <TextField
                  fullWidth
                  size="small"
                  value={value.applyEmail || ''}
                  onChange={e => updateField('applyEmail', e.target.value)}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={value.requireResume ?? true}
                      onChange={e => updateField('requireResume', e.target.checked)}
                    />
                  }
                  label="Require applicants to attach a resume"
                />
              </Box>
            ),
          })}
    </>
  );

  if (!wrapInGroup) {
    return fields;
  }

  return (
    <Group
      title={hideGroupTitle ? undefined : 'Job Autofill for LinkedIn (optional)'}
      bg="white"
      id="job_form_recruiter_lite"
      isCollapsible={!hideGroupTitle}
      defaultExpanded={false}
    >
      {fields}
    </Group>
  );
};

export default RecruiterLiteFieldsForm;
